import {
    HiOutlineGlobeAlt,
    HiOutlineShoppingCart,
    HiOutlineDesktopComputer,
    HiOutlineDeviceMobile,
    HiOutlineSpeakerphone,
    HiOutlineColorSwatch,
} from "react-icons/hi";

/**
 * The six main ProDevop services.
 *
 * Slugs are canonical URLs — changing one requires a redirect in App.js and an
 * update to backend/seo_routes.py. Legacy slugs are mapped in
 * LEGACY_SERVICE_SLUGS at the bottom of this file.
 *
 * Every service follows the same content shape so ServiceDetailPage can render
 * one reusable layout without repeating sections:
 *
 *   heroTagline     - short line under the page title
 *   description     - overview paragraph
 *   forWho[]        - who the service is for
 *   problems[]      - business problems it solves
 *   capabilities[]  - main capabilities (title + description)
 *   features[]      - concrete deliverables
 *   technologies[]  - tools genuinely used
 *   timeline        - realistic estimate, always scope-dependent
 *   costFactors[]   - what affects the price (no public pricing anywhere)
 *   faqs[]          - questions specific to this service
 *   related[]       - slugs of genuinely relevant other services
 *
 * Payment approach and post-launch support are identical across all services,
 * so they live in SERVICE_COMMON below rather than being repeated per service.
 */

/** Shared content rendered on every service page. */
export const SERVICE_COMMON = {
    payment: {
        title: "How payment works",
        description:
            "Depending on the project we offer milestone-based payments tied to delivery stages, or installment arrangements. The structure varies by project, so we cannot promise identical terms for every engagement. Your payment schedule is confirmed in writing in the quotation or agreement before development begins.",
    },
    support: {
        title: "Support after launch",
        description:
            "Every project includes three months of free post-launch support covering defects, technical issues, the functionality we agreed, and reasonable help settling in after launch. It does not include unlimited new features or major changes to the original scope. After three months, paid maintenance is available — monthly, annual, or on request — arranged to suit the project.",
    },
    /** Cost factors that apply to every project, shown alongside service-specific ones. */
    sharedCostFactors: [
        "Overall scope of work",
        "Custom functionality and business rules",
        "Third-party integrations",
        "Whether your content is ready",
        "Testing requirements",
        "How quickly you need it delivered",
    ],
};

export const SERVICES = [
    {
        id: "custom-website-development",
        slug: "custom-website-development",
        icon: HiOutlineGlobeAlt,
        title: "Custom Website Development",
        heroTagline: "A website built around your business — not a template you have to work around.",
        shortDescription:
            "Fully custom, responsive business websites built around your goals — fast, search-friendly, and easy to manage.",
        description:
            "We design and build custom websites for startups, professionals, and growing businesses in Sri Lanka. Every site is planned around your goals, the pages you actually need, your content, and your budget, with proper attention to mobile usability, speed, search visibility, and security.",
        forWho: [
            "Startups building their first web presence",
            "Small and medium businesses",
            "Professionals and consultants",
            "Schools, academies, and training providers",
            "Restaurants, hotels, and tourism companies",
            "Retail businesses and healthcare providers",
            "NGOs and organisations needing a fully custom site",
        ],
        problems: [
            {
                problem: "Customers cannot find you online",
                solution:
                    "We build with clean structure, fast loading, and search-friendly markup so your business can be found and understood by both people and search engines.",
            },
            {
                problem: "Your current site looks dated or does not work on phones",
                solution:
                    "Every site we build is designed mobile-first and tested across screen sizes, because most Sri Lankan visitors arrive on a phone.",
            },
            {
                problem: "You cannot update your own content",
                solution:
                    "We set up the pages you need to change yourself, so routine updates do not require a developer every time.",
            },
            {
                problem: "Enquiries get lost",
                solution:
                    "Contact and enquiry forms are wired to reach you reliably, with confirmation to the visitor so nobody wonders whether their message arrived.",
            },
        ],
        capabilities: [
            {
                title: "Custom design",
                description: "Layout and visual design created for your business rather than adapted from a template.",
            },
            {
                title: "Responsive across devices",
                description: "Tested on phones, tablets, and desktops so the experience holds up everywhere.",
            },
            {
                title: "Search-ready structure",
                description: "Proper headings, metadata, and clean URLs so search engines can index your pages correctly.",
            },
            {
                title: "Editable content",
                description: "The pages you need to keep current can be managed without touching code.",
            },
        ],
        features: [
            "Custom responsive website design",
            "React and modern front-end development",
            "Content management for the pages you update",
            "Contact and enquiry forms with email notifications",
            "Performance optimisation and Core Web Vitals",
            "Search-friendly structure and metadata",
            "Analytics-ready setup",
            "Three months of free post-launch support",
        ],
        technologies: ["React", "Django", "Node.js", "Tailwind CSS", "PostgreSQL"],
        timeline: {
            estimate: "2 – 4 weeks",
            note: "A straightforward business website is usually delivered within this range. Larger sites with more pages, custom functionality, or integrations take longer. The final schedule depends on scope, features, how quickly content and feedback are provided, and testing.",
        },
        costFactors: [
            "Number of pages",
            "Depth of custom design work",
            "Content management requirements",
            "Forms and enquiry workflows",
            "Multi-language requirements",
        ],
        faqs: [
            {
                question: "Do you use WordPress or a template?",
                answer:
                    "No. We build custom websites so the site does exactly what your business needs and can grow with you, rather than fitting your business around a template's limitations.",
            },
            {
                question: "Can I update the website myself after launch?",
                answer:
                    "Yes. We set up the pages and sections you need to keep current so you can edit them without a developer. We agree during planning which parts should be editable.",
            },
            {
                question: "Do I need to provide the content and images?",
                answer:
                    "Content you provide always represents your business best, and having it ready speeds the project up considerably. If you are not sure what to write, we will guide you on what each page needs.",
            },
        ],
        related: ["custom-ecommerce-development", "ui-ux-design", "digital-marketing-branding"],
        color: "purple",
    },

    {
        id: "custom-ecommerce-development",
        slug: "custom-ecommerce-development",
        icon: HiOutlineShoppingCart,
        title: "Custom E-Commerce Development",
        heroTagline: "A custom-coded online store built around how your business actually sells.",
        shortDescription:
            "Custom-coded online stores with product catalogues, carts, customer accounts, and order management.",
        description:
            "We build custom-coded e-commerce websites — not template stores. Product catalogues, shopping carts, customer accounts, order workflows, and an administration panel are all built around how your business actually sells, so you are not forced into someone else's checkout process.",
        forWho: [
            "Retail shops moving online",
            "Boutiques and fashion brands",
            "Wholesalers and manufacturers",
            "Supermarkets and pharmacies",
            "Food businesses",
            "Electronics sellers",
            "Home-based and product-based startups",
        ],
        problems: [
            {
                problem: "Template platforms do not fit how you sell",
                solution:
                    "Because your store is custom-coded, product options, pricing rules, and the checkout flow are built around your actual business rather than a platform's assumptions.",
            },
            {
                problem: "Monthly platform fees keep rising as you grow",
                solution:
                    "A custom store is yours. There is no per-transaction platform fee or subscription tier that increases as your sales grow.",
            },
            {
                problem: "Managing orders and stock is manual and error-prone",
                solution:
                    "An administration panel gives you orders, stock levels, and customer records in one place, so you are not tracking sales across notebooks and messages.",
            },
            {
                problem: "Customers abandon a confusing checkout",
                solution:
                    "We design a short, clear checkout that works properly on phones, where most Sri Lankan shoppers buy.",
            },
        ],
        capabilities: [
            {
                title: "Product catalogue",
                description: "Categories, product variants, images, stock levels, and pricing managed from one panel.",
            },
            {
                title: "Cart and checkout",
                description: "A mobile-first shopping flow designed to be short and clear rather than clever.",
            },
            {
                title: "Customer accounts",
                description: "Registration, order history, and saved details so returning customers buy faster.",
            },
            {
                title: "Order management",
                description: "An admin panel for processing orders, updating status, and keeping stock accurate.",
            },
        ],
        features: [
            "Custom-coded online store",
            "Product catalogue with categories and variants",
            "Shopping cart and checkout workflow",
            "Customer accounts and order history",
            "Bank transfer payment workflow",
            "Order and stock management panel",
            "Mobile-first shopping experience",
            "Three months of free post-launch support",
        ],
        technologies: ["React", "Django", "PostgreSQL", "Tailwind CSS"],
        timeline: {
            estimate: "2 – 4 weeks",
            note: "This is our typical range for a custom e-commerce build. The final timeline depends on scope, the number of products, features, integrations, content availability, revisions, testing, and overall project complexity.",
        },
        costFactors: [
            "Number of products and categories",
            "Product variants and pricing rules",
            "Customer account features",
            "Delivery and courier requirements",
            "Reporting and admin requirements",
            "Migration of existing product data",
        ],
        faqs: [
            {
                question: "Do you build Shopify or WooCommerce stores?",
                answer:
                    "No. We build custom-coded e-commerce websites only. That means the store is built specifically for your business, you own it outright, and there are no platform subscription tiers or per-transaction platform fees.",
            },
            {
                question: "Which payment methods can customers use?",
                answer:
                    "Bank transfer is the payment method we currently support. If you need an online payment gateway, tell us during planning and we will discuss what is realistic for your project rather than promising something we have not implemented.",
            },
            {
                question: "Can the store connect to courier or delivery services?",
                answer:
                    "Courier and delivery integrations can be considered where your business genuinely requires them and the service supports it. We assess this during planning and confirm what is achievable before quoting.",
            },
            {
                question: "Can you move my existing products across?",
                answer:
                    "Usually yes. Data migration depends on the format your current product information is in. We review it during planning, and it is one of the factors that affects the project cost.",
            },
        ],
        related: ["custom-website-development", "pos-inventory-systems", "digital-marketing-branding"],
        color: "cyan",
    },

    {
        id: "pos-inventory-systems",
        slug: "pos-inventory-systems",
        icon: HiOutlineDesktopComputer,
        title: "POS & Inventory Systems",
        heroTagline: "Billing, stock, and reporting built around how your business actually runs.",
        shortDescription:
            "Point of sale and stock control systems for shops, restaurants, pharmacies, and multi-branch businesses.",
        description:
            "We build POS and inventory systems for retail shops, supermarkets, restaurants, pharmacies, wholesalers, and multi-branch businesses in Sri Lanka. Billing, stock control, reporting, and user permissions are configured around your day-to-day workflow rather than forcing your staff to adapt to fixed software.",
        forWho: [
            "Retail shops and supermarkets",
            "Restaurants and cafés",
            "Pharmacies",
            "Wholesalers and distributors",
            "Warehouses and hardware stores",
            "Fashion and electronics shops",
            "Service centres and multi-branch businesses",
        ],
        problems: [
            {
                problem: "Stock records never match what is on the shelf",
                solution:
                    "Stock updates as sales happen, with low-stock alerts, so you find out before you run out rather than after.",
            },
            {
                problem: "You cannot tell which products actually make money",
                solution:
                    "Sales and stock reports show what is selling and what is sitting, so purchasing decisions are based on figures rather than memory.",
            },
            {
                problem: "Staff can see or change things they should not",
                solution:
                    "Role-based access means each staff member sees only what their job requires, with sensitive functions restricted.",
            },
            {
                problem: "Branches operate as separate islands",
                solution:
                    "Where required, we build multi-branch support so stock and sales across locations are visible in one place.",
            },
        ],
        capabilities: [
            {
                title: "Point of sale and billing",
                description: "Fast day-to-day billing with receipts and invoices suited to your counter workflow.",
            },
            {
                title: "Stock control",
                description: "Live stock levels, low-stock alerts, and adjustments across products and locations.",
            },
            {
                title: "Reporting",
                description: "Sales, stock, and product reports that answer the questions you actually ask.",
            },
            {
                title: "User roles and permissions",
                description: "Separate access for cashiers, supervisors, and owners, with restricted functions.",
            },
        ],
        features: [
            "Point of sale and billing",
            "Invoice and receipt generation",
            "Stock control and low-stock alerts",
            "Inventory management across locations",
            "Sales and stock reporting",
            "Multi-user role-based access control",
            "Customer and supplier records",
            "Multi-branch support where required",
        ],
        technologies: ["React", "Django", "Python", "PostgreSQL", "Electron"],
        timeline: {
            estimate: "4 – 8 weeks",
            note: "POS and inventory systems take longer than websites because they must match your operations precisely. The final schedule depends on the number of features, user roles, branches, reporting requirements, integrations, data migration, and testing.",
        },
        costFactors: [
            "Number of users and user roles",
            "Number of branches or locations",
            "Reporting requirements",
            "Barcode and hardware requirements",
            "Migration of existing stock and product data",
            "Staff training requirements",
        ],
        faqs: [
            {
                question: "How much does a POS system cost in Sri Lanka?",
                answer:
                    "It depends on users, branches, devices, reports, integrations, inventory requirements, and custom features. We review how your business operates first, then provide a written scope and quotation, so you pay for the capabilities you actually need rather than a fixed package.",
            },
            {
                question: "How do I choose the right POS system for my business?",
                answer:
                    "The right system matches your real sales, stock, invoicing, reporting, user, device, and branch requirements without unnecessary complexity. We review those workflows before recommending either a standard configuration or a fully custom solution.",
            },
            {
                question: "Can it work across several branches?",
                answer:
                    "Yes, where your business requires it. Multi-branch support affects the scope and cost, so we confirm your branch structure and what visibility you need during planning.",
            },
            {
                question: "Can you move data from our current system?",
                answer:
                    "Usually yes, depending on the format your existing product, stock, and customer data is in. We assess this during planning — data migration is one of the factors that affects project cost.",
            },
        ],
        related: ["custom-ecommerce-development", "mobile-application-development", "custom-website-development"],
        color: "blue",
    },

    {
        id: "mobile-application-development",
        slug: "mobile-application-development",
        icon: HiOutlineDeviceMobile,
        title: "Mobile Application Development",
        heroTagline: "Android and iOS applications built from one codebase.",
        shortDescription:
            "Android and iOS applications for delivery, booking, education, healthcare, and field-service businesses.",
        description:
            "We develop mobile applications for Android and iOS using React Native, so a single codebase serves both platforms. This suits delivery services, booking platforms, educational organisations, healthcare providers, restaurants, and businesses with teams working in the field.",
        forWho: [
            "Startups launching a mobile product",
            "Delivery and transport services",
            "Booking and appointment platforms",
            "Educational organisations",
            "Healthcare providers",
            "Restaurants and retail businesses",
            "Field-service teams and event organisers",
        ],
        problems: [
            {
                problem: "Building separately for Android and iOS costs twice",
                solution:
                    "React Native lets one codebase serve both platforms, which keeps both the initial build and future updates more affordable.",
            },
            {
                problem: "Your team in the field cannot access your systems",
                solution:
                    "We build apps that connect to your existing systems through APIs, so field staff work with the same live data as the office.",
            },
            {
                problem: "Customers forget to come back",
                solution:
                    "Push notifications let you reach customers directly about orders, appointments, and updates.",
            },
            {
                problem: "Connectivity is unreliable where your app is used",
                solution:
                    "Where the workflow requires it, we handle offline behaviour so work is not lost when the signal drops.",
            },
        ],
        capabilities: [
            {
                title: "Cross-platform build",
                description: "One codebase producing both Android and iOS applications.",
            },
            {
                title: "System integration",
                description: "Connecting the app to your website, POS, or existing business systems through APIs.",
            },
            {
                title: "Store deployment",
                description: "Preparing and submitting your app to the Play Store and App Store.",
            },
            {
                title: "User accounts and roles",
                description: "Registration, login, and role-based access for customers, staff, or partners.",
            },
        ],
        features: [
            "Cross-platform Android and iOS applications",
            "React Native development",
            "Play Store and App Store deployment",
            "Push notifications",
            "API integration with your existing systems",
            "Offline handling where required",
            "User accounts and role-based access",
            "Analytics and crash reporting",
        ],
        technologies: ["React Native", "Django", "Firebase", "REST APIs"],
        timeline: {
            estimate: "6 – 12 weeks",
            note: "Mobile applications take longer because they involve two platforms, store review processes, and device testing. The final schedule depends on the number of screens, user roles, integrations, offline requirements, testing, and store approval times, which are outside our control.",
        },
        costFactors: [
            "Number of screens and user roles",
            "Backend and API requirements",
            "Offline functionality",
            "Push notifications and real-time features",
            "Device and platform testing scope",
            "App store account setup and submission",
        ],
        faqs: [
            {
                question: "Will the app work on both Android and iPhone?",
                answer:
                    "Yes. We build with React Native so one codebase produces applications for both platforms, which keeps development and future updates more affordable than building each separately.",
            },
            {
                question: "Do you handle publishing to the app stores?",
                answer:
                    "Yes, we prepare and submit your app to the Play Store and App Store. You will need your own developer accounts, and store review times are set by Google and Apple rather than by us.",
            },
            {
                question: "Can the app connect to my existing website or POS system?",
                answer:
                    "Yes, provided your existing system can expose an API or we build one for it. Integration requirements affect the scope, so we assess this during planning.",
            },
        ],
        related: ["pos-inventory-systems", "ui-ux-design", "custom-website-development"],
        color: "green",
    },

    {
        id: "digital-marketing-branding",
        slug: "digital-marketing-branding",
        icon: HiOutlineSpeakerphone,
        title: "Digital Marketing & Branding",
        heroTagline: "A clear brand and a way for the right customers to find you.",
        shortDescription:
            "Brand identity, social media, and search visibility that bring enquiries to your business.",
        description:
            "We help startups and local businesses build a clear brand and reach the right customers online. This covers brand identity, social media presence, search visibility, and paid campaigns — planned around your goals and budget rather than sold as a fixed package.",
        forWho: [
            "Startups establishing a brand",
            "Local businesses and retail shops",
            "Restaurants, hotels, and travel companies",
            "Schools, academies, and healthcare providers",
            "E-commerce brands",
            "Real estate and professional services",
            "Any business needing more enquiries and visibility",
        ],
        problems: [
            {
                problem: "Your business looks different everywhere it appears",
                solution:
                    "We build a consistent brand identity — logo, colours, and typography — applied across your website, social media, and printed material.",
            },
            {
                problem: "Nobody finds you when they search",
                solution:
                    "Search optimisation improves how your pages are structured and described, so your business appears for the terms your customers actually use.",
            },
            {
                problem: "You post on social media but nothing happens",
                solution:
                    "We plan content around what your customers care about and what leads to enquiries, rather than posting for the sake of activity.",
            },
            {
                problem: "Ad spend disappears with nothing to show",
                solution:
                    "Campaigns are set up with clear targeting and tracked against enquiries, so you can see what your spend produced.",
            },
        ],
        capabilities: [
            {
                title: "Brand identity",
                description: "Logo, colours, typography, and brand assets that work consistently across every channel.",
            },
            {
                title: "Search optimisation",
                description: "Improving structure, content, and metadata so your pages can rank for relevant searches.",
            },
            {
                title: "Social media",
                description: "Account setup, content planning, and ongoing management where you need the support.",
            },
            {
                title: "Paid campaigns",
                description: "Google Ads and Meta Ads with clear targeting and reporting against results.",
            },
        ],
        features: [
            "Brand identity and positioning",
            "Logo and brand asset design",
            "Search engine optimisation (SEO)",
            "Social media setup and management",
            "Google Ads and Meta Ads campaigns",
            "Content planning and creation",
            "Landing pages built for conversions",
            "Performance reporting",
        ],
        technologies: ["Google Analytics", "Google Ads", "Meta Business Suite", "Canva", "Figma"],
        timeline: {
            estimate: "1 – 3 weeks to set up, then ongoing",
            note: "Branding and initial setup are usually completed within this range. Marketing itself is ongoing rather than a one-off delivery — search visibility in particular builds over months, not days. We agree the scope and review points up front so you know what to expect.",
        },
        costFactors: [
            "Whether branding is being created or refreshed",
            "Number of channels being managed",
            "Volume of content required",
            "Whether campaigns are ongoing or one-off",
            "Advertising spend, which is paid separately to the platforms",
            "Reporting frequency",
        ],
        faqs: [
            {
                question: "How quickly will I see results from SEO?",
                answer:
                    "Search visibility builds over months rather than days, and no honest agency can guarantee a position or a date. Paid campaigns produce traffic much faster. We will tell you which is the better fit for your goals and budget rather than promising rankings we cannot control.",
            },
            {
                question: "Is advertising spend included in your fee?",
                answer:
                    "No. Advertising budget is paid directly to Google or Meta and is separate from our fee for planning and managing the campaigns. We agree both figures with you before starting.",
            },
            {
                question: "Can you design a logo and brand for a new business?",
                answer:
                    "Yes. For startups we often begin with brand identity, then apply it consistently across the website and social media so everything looks like one business from day one.",
            },
        ],
        related: ["custom-website-development", "ui-ux-design", "custom-ecommerce-development"],
        color: "orange",
    },

    {
        id: "ui-ux-design",
        slug: "ui-ux-design",
        icon: HiOutlineColorSwatch,
        title: "UI/UX Design",
        heroTagline: "Interfaces that are clear to use, designed before a line of code is written.",
        shortDescription:
            "Interface and experience design for websites, apps, and business systems — from wireframes to final screens.",
        description:
            "We design interfaces that are clear to use and consistent across screens. This suits startups planning a new product, businesses redesigning an existing website or app, and teams building POS or business systems that staff use every day.",
        forWho: [
            "Startups planning a new product",
            "Companies planning a website",
            "E-commerce businesses",
            "Tourism and booking platforms",
            "POS and business-system projects",
            "Mobile application projects",
            "Schools, universities, and campuses",
            "Businesses redesigning an existing product",
        ],
        problems: [
            {
                problem: "Users cannot work out how to do the thing they came for",
                solution:
                    "We map the tasks people actually need to complete and design the shortest sensible path to each one.",
            },
            {
                problem: "Development starts before anyone agrees what it looks like",
                solution:
                    "Wireframes and prototypes let you see and approve the design before development begins, which is far cheaper than changing it afterwards.",
            },
            {
                problem: "Every screen looks slightly different",
                solution:
                    "A design system defines buttons, forms, and layouts once, so the product stays consistent as it grows.",
            },
            {
                problem: "Staff make mistakes using your internal system",
                solution:
                    "For POS and business systems we design around the daily workflow, reducing the steps and the opportunities for error.",
            },
        ],
        capabilities: [
            {
                title: "Research and planning",
                description: "Understanding your users and mapping the tasks the product must support.",
            },
            {
                title: "Wireframes and prototypes",
                description: "Seeing and testing the structure before development starts.",
            },
            {
                title: "Interface design",
                description: "High-fidelity screens for every state, not just the ideal case.",
            },
            {
                title: "Design system",
                description: "Reusable components so the product stays consistent as it grows.",
            },
        ],
        features: [
            "User research and requirement mapping",
            "Wireframing and prototyping",
            "High-fidelity interface design",
            "Design system and component library",
            "Responsive and mobile-first layouts",
            "Accessibility considerations",
            "Design handoff to development",
            "Redesign of existing products",
        ],
        technologies: ["Figma", "Adobe Illustrator", "Photoshop"],
        timeline: {
            estimate: "2 – 4 weeks",
            note: "Design for a typical website or app is usually completed within this range. Larger products with many screens, multiple user roles, or a full design system take longer. The final schedule depends on the number of screens, research requirements, and revision rounds.",
        },
        costFactors: [
            "Number of screens and user flows",
            "Depth of user research required",
            "Whether a design system is needed",
            "Number of revision rounds",
            "Whether it is a new product or a redesign",
            "Prototype interactivity requirements",
        ],
        faqs: [
            {
                question: "Can you design something another developer will build?",
                answer:
                    "Yes. We deliver the design files and a documented handoff so any competent development team can build from them. You are not obliged to have us build it.",
            },
            {
                question: "Do I need design if you are also building my website?",
                answer:
                    "Design is part of every build we do. A separate UI/UX engagement makes sense when a product is complex, involves several user roles, or when you want the design agreed and validated before committing to development.",
            },
            {
                question: "How many rounds of changes are included?",
                answer:
                    "We agree the number of revision rounds in the quotation before work begins, so expectations are clear on both sides. It is one of the factors that affects the project cost.",
            },
        ],
        related: ["custom-website-development", "mobile-application-development", "digital-marketing-branding"],
        color: "purple",
    },
];

/**
 * IT Consulting has no dedicated landing page.
 * It is presented on the main Services page as a consultation-based service.
 */
export const IT_CONSULTING = {
    title: "IT Consulting",
    description:
        "If you are not sure what your business needs, we can review your current setup and advise on the sensible next step — which system to build first, whether to improve or replace what you already have, and what a realistic plan looks like.",
    note: "Availability and scope depend on your requirements and our current commitments. Contact us to discuss whether a consultation is the right starting point for your project.",
    points: [
        "Reviewing your current systems and identifying gaps",
        "Advising which service to start with and in what order",
        "Assessing whether to improve or rebuild existing software",
        "Planning a realistic phased approach for your budget",
    ],
};

/**
 * Old service slugs mapped to their replacements.
 * Used to build redirects so existing search rankings and bookmarks survive.
 * IT Consulting maps to null and falls back to /services.
 */
export const LEGACY_SERVICE_SLUGS = {
    "web-development": "custom-website-development",
    "mobile-apps": "mobile-application-development",
    "custom-software": "pos-inventory-systems",
    "digital-marketing": "digital-marketing-branding",
    "it-consulting": null, // → /services
};
